import React from 'react';
import { GenericFormPage } from '../../components/generic';
import { useMarketingClient, MarketingClient } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const configWithOptions = (() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'clientCountryId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Country',
          placeholder: 'Select country',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/p-country',
          labelField: 'pc_descr',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'clientTaxAuthority') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Tax Authority',
          placeholder: 'Select tax authority',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/p-tax-authority',
          labelField: 'pt_descr',
          valueField: 'id' as const,
        };
      }
      return field;
    });

    // Form data allows foreign keys as string for select components
    type FormMarketingClient = Omit<MarketingClient, 'clientCountryId' | 'clientTaxAuthority'> & {
      clientCountryId?: string | number;
      clientTaxAuthority?: string | number;
    };

    // Custom mapItemToFormData that converts foreign keys to string for select
    const customMapItemToFormData = (item: MarketingClient): Partial<FormMarketingClient> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientCountryId: baseData.clientCountryId ? String(baseData.clientCountryId) : '',
        clientTaxAuthority: baseData.clientTaxAuthority ? String(baseData.clientTaxAuthority) : '',
        isClient: baseData.isClient ?? false,
      };
    };

    // Custom getInitialFormData
    const customGetInitialFormData = (): Partial<FormMarketingClient> => {
      return {
        ...getInitialFormData(),
        clientCountryId: '',
        clientTaxAuthority: '',
        isClient: false, // Always false for marketing
      };
    };

    // Helper function to convert string to number or undefined
    const convertToNumber = (value: string | number | undefined): number | undefined => {
      if (value === undefined || value === null || String(value) === '') {
        return undefined;
      }
      return Number(value);
    };

    // Custom create/update handlers that convert foreign key strings back to numbers
    const customCreate = async (data: Partial<FormMarketingClient>) => {
      const processedData: Partial<MarketingClient> = {
        ...data,
        clientCountryId: convertToNumber(data.clientCountryId),
        clientTaxAuthority: convertToNumber(data.clientTaxAuthority),
        isClient: false, // Always false for marketing
      };
      return await moduleConfig.form.create(processedData);
    };

    const customUpdate = async (id: number, data: Partial<FormMarketingClient>) => {
      const processedData: Partial<MarketingClient> = {
        ...data,
        clientCountryId: convertToNumber(data.clientCountryId),
        clientTaxAuthority: convertToNumber(data.clientTaxAuthority),
        isClient: data.isClient ?? false,
      };
      return await moduleConfig.form.update(id, processedData);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<MarketingClient>,
      mapItemToFormData: customMapItemToFormData as (item: MarketingClient) => Partial<MarketingClient>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<MarketingClient>) => Promise<MarketingClient>,
        update: customUpdate as (id: number, data: Partial<MarketingClient>) => Promise<MarketingClient>,
      },
    };
  })();

  return (
    <GenericFormPage<MarketingClient>
      useData={useMarketingClient}
      config={configWithOptions}
    />
  );
};

export default Form;

